import type React from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import Cookies from 'js-cookie';
import { useTracker } from '@archipro-website/tracker';
import { useRootData } from '@modules/root';
import { isExternalURL, urlEventDiscovery } from '~/utils/urlHelper';
import { enhanceProfessionalWebsiteLink } from '../util/websiteTracking';

interface TrackableItemType {
    professionalID?: number;
    professionalTitle?: string;
    itemType?: string;
    link?: string;
}

interface WebsiteTrackingProxyOptions {
    attachUTM?: boolean;
    processRelativeURL?: boolean;
    preventOpenLink?: boolean;
}

const isSamePageHashHref = (href: string): boolean => {
    const trimmed = href.trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('#')) return true;

    try {
        const target = new URL(trimmed, window.location.href);
        return (
            target.origin === window.location.origin &&
            target.pathname === window.location.pathname &&
            target.hash.length > 1
        );
    } catch {
        return false;
    }
};

export const useWebsiteTrackingProxy = (
    item: TrackableItemType = {},
    options: WebsiteTrackingProxyOptions = {},
): React.EventHandler<React.MouseEvent> => {
    const { config } = useRootData();
    const tracker = useTracker();
    const navigate = useNavigate();
    const { attachUTM = true, processRelativeURL, preventOpenLink } = options;
    const {
        professionalID,
        professionalTitle,
        itemType,
        link: professionalLink,
    } = item;

    return useCallback(
        (e: React.MouseEvent) => {
            if (e.isPropagationStopped()) {
                return;
            }

            const link = urlEventDiscovery(e);
            if (!link) {
                return;
            }

            let href = link.getAttribute('href') || '';
            const openInNewTab = link.getAttribute('target') === '_blank';
            const isExternal = isExternalURL(href);
            const AP_ID = Cookies.get(config.cookies.cookieName) || null;

            // Same-page hashes (`#product-files` or `/product/slug#product-files`)
            // are in-page anchors. They fail `isExternalURL`, so the branch
            // below would preventDefault and send them through `/forward`,
            // which drops the hash.
            if (isSamePageHashHref(href)) {
                return;
            }

            e.stopPropagation();

            if (preventOpenLink) {
                e.preventDefault();
                return;
            }

            // If the link is not external, we let react-router handle the navigation
            if (!isExternal) {
                e.preventDefault();

                const linkTarget = new URL(href, window.location.origin);
                const next = `${linkTarget.pathname}${linkTarget.search}${linkTarget.hash}`;
                if (!processRelativeURL || !professionalLink) {
                    if (openInNewTab) {
                        window.open(next);
                    } else {
                        navigate(next);
                    }
                    return;
                }

                const redirectURL = new URL('/forward', window.location.origin);

                // If the link is relative, we use `/redirect` to process the URL
                const urlWithUTM =
                    enhanceProfessionalWebsiteLink(
                        linkTarget.href,
                        true,
                        AP_ID,
                    ) || '';

                redirectURL.searchParams.append('link', professionalLink);

                if (urlWithUTM) {
                    const url = new URL(urlWithUTM);
                    const qs = url.searchParams.toString();
                    if (qs) {
                        redirectURL.searchParams.append('qs', qs);
                    }
                    redirectURL.searchParams.append('path', url.pathname);
                } else {
                    redirectURL.searchParams.append(
                        'path',
                        linkTarget.pathname,
                    );
                }

                if (openInNewTab) {
                    window.open(redirectURL.href);
                } else {
                    location.href = redirectURL.href;
                }

                return;
            }

            if (attachUTM) {
                href = enhanceProfessionalWebsiteLink(href, true, AP_ID) || '';
            }

            tracker.log('Website', {
                url: new URL(window.location.href),
                label: professionalTitle,
                professionalId: professionalID,
                data: {
                    ExternalURL: href,
                    LibraryItemID: professionalID,
                    ProfessionalID: professionalID,
                    itemType,
                    ExtraData: {
                        MouseButton: e.button,
                    },
                },
            });

            // The anchor `urlEventDiscovery` resolved, not `e.target`. CMS
            // copy wraps its links around <strong>, <em> and <img>, so the
            // press usually lands on one of those — and testing `e.target`
            // sent every such press down the branch below, which navigates.
            // That is merely rude on a left click (a modified one lost its
            // new tab) and wrong on the others: this handler is also bound to
            // contextmenu and auxclick, where it meant a right click left the
            // page instead of offering Copy link, and a middle click replaced
            // the tab instead of opening one.
            //
            // Rewriting in place and standing aside keeps the browser's own
            // behaviour for all three.
            if (link instanceof HTMLAnchorElement) {
                link.href = href;
                return;
            }

            e.preventDefault();
            if (openInNewTab) {
                window?.open(href);
            } else {
                location.href = href;
            }
        },
        [
            professionalID,
            professionalTitle,
            itemType,
            attachUTM,
            tracker,
            navigate,
            processRelativeURL,
            professionalLink,
            preventOpenLink,
            config,
        ],
    );
};
